import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import authService from '../services/authService';
import { AlertIcon, CheckCircleIcon, SecurityIcon, ShieldIcon } from '../components/Icons';

const Profile = () => {
    const { admin, updateAdminData } = useAuth();
    const [formData, setFormData] = useState({
        username: '',
        email: '',
        password: '',
        confirmPassword: ''
    });
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');

    // 2FA state
    const [show2FASetup, setShow2FASetup] = useState(false);
    const [twoFAData, setTwoFAData] = useState(null);
    const [twoFAToken, setTwoFAToken] = useState('');
    const [twoFALoading, setTwoFALoading] = useState(false);

    useEffect(() => {
        if (admin) {
            setFormData({
                username: admin.username || '',
                email: admin.email || '',
                password: '',
                confirmPassword: ''
            });
        }
    }, [admin]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setError('');
        setSuccess('');
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        if (!formData.password) {
            setError('Please enter a new password');
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                password: formData.password
            };

            const updatedAdmin = await authService.updateProfile(updateData);
            updateAdminData(updatedAdmin);
            setSuccess('Password updated successfully');
            setFormData(prev => ({ ...prev, password: '', confirmPassword: '' }));
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Failed to update password');
        } finally {
            setLoading(false);
        }
    };

    const handleSetup2FA = async () => {
        setTwoFALoading(true);
        try {
            const data = await authService.setup2FA();
            setTwoFAData(data);
            setShow2FASetup(true);
        } catch (err) {
            setError('Failed to setup 2FA');
        } finally {
            setTwoFALoading(false);
        }
    };

    const handleEnable2FA = async () => {
        if (!twoFAToken) return;
        setTwoFALoading(true);
        try {
            await authService.enable2FA(twoFAToken);
            updateAdminData({ is_two_factor_enabled: true });
            setShow2FASetup(false);
            setTwoFAData(null);
            setTwoFAToken('');
            setSuccess('2FA enabled successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to enable 2FA');
        } finally {
            setTwoFALoading(false);
        }
    };

    const handleDisable2FA = async () => {
        const token = window.prompt('Please enter your 2FA token to disable 2FA:');
        if (!token) return;

        setTwoFALoading(true);
        try {
            await authService.disable2FA(token);
            updateAdminData({ is_two_factor_enabled: false });
            setSuccess('2FA disabled successfully');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to disable 2FA');
        } finally {
            setTwoFALoading(false);
        }
    };

    return (
        <div className="page-container" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem' }}>
            <div style={{ marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 700, color: 'var(--text-primary)' }}>Account Settings</h1>
                <p style={{ color: 'var(--text-secondary)' }}>Manage your personal information and security settings.</p>
            </div>

            {error && (
                <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem', color: 'var(--error)', backgroundColor: 'rgba(239, 68, 68, 0.1)', border: '1px solid var(--error)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <AlertIcon size={20} />
                    <span>{error}</span>
                </div>
            )}

            {success && (
                <div className="card" style={{ padding: '1rem', marginBottom: '1.5rem', color: 'var(--success)', backgroundColor: 'rgba(34, 197, 94, 0.1)', border: '1px solid var(--success)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <CheckCircleIcon size={20} />
                    <span>{success}</span>
                </div>
            )}

            <div className="card" style={{ padding: '2rem', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '1.5rem' }}>Profile Information</h2>
                <form onSubmit={handleProfileSubmit}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
                        <div>
                            <label className="label">Username (Read-only)</label>
                            <input
                                type="text"
                                name="username"
                                className="input"
                                value={formData.username}
                                disabled
                                style={{ backgroundColor: 'var(--bg-soft)', opacity: 0.7, cursor: 'not-allowed' }}
                            />
                        </div>
                        <div>
                            <label className="label">Email Address (Read-only)</label>
                            <input
                                type="email"
                                name="email"
                                className="input"
                                value={formData.email}
                                disabled
                                style={{ backgroundColor: 'var(--bg-soft)', opacity: 0.7, cursor: 'not-allowed' }}
                            />
                        </div>
                        <div>
                            <label className="label">New Password</label>
                            <input
                                type="password"
                                name="password"
                                className="input"
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Enter new password"
                                required
                            />
                        </div>
                        <div>
                            <label className="label">Confirm New Password</label>
                            <input
                                type="password"
                                name="confirmPassword"
                                className="input"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                placeholder="Confirm new password"
                                required
                            />
                        </div>
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                </form>
            </div>

            <div className="card" style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                    <div style={{ color: 'var(--primary)', padding: '0.5rem', background: 'var(--bg-soft)', borderRadius: '0.5rem' }}>
                        <ShieldIcon size={24} />
                    </div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Two-Factor Authentication</h2>
                </div>
                
                <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
                    Add an extra layer of security to your account by requiring a verification code when you log in.
                </p>

                {admin?.is_two_factor_enabled ? (
                    <div style={{ padding: '1.5rem', background: 'var(--bg-soft)', borderRadius: '0.75rem', border: '1px solid var(--success)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--success)', fontWeight: 600, marginBottom: '0.25rem' }}>
                                    <CheckCircleIcon size={16} />
                                    <span>2FA is enabled</span>
                                </div>
                                <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                    Your account is protected with an additional security layer.
                                </p>
                            </div>
                            <button 
                                className="btn btn-danger" 
                                onClick={handleDisable2FA}
                                disabled={twoFALoading}
                            >
                                Disable 2FA
                            </button>
                        </div>
                    </div>
                ) : (
                    <div>
                        {!show2FASetup ? (
                            <button 
                                className="btn btn-primary" 
                                onClick={handleSetup2FA}
                                disabled={twoFALoading}
                            >
                                Setup 2FA
                            </button>
                        ) : (
                            <div style={{ padding: '1.5rem', background: 'var(--bg-soft)', borderRadius: '0.75rem', border: '1px solid var(--border-light)' }}>
                                <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '1rem' }}>Configure Authenticator</h3>
                                <p style={{ fontSize: '0.875rem', marginBottom: '1.5rem' }}>
                                    1. Scan the QR code below with your authenticator app (e.g., Google Authenticator, Microsoft Authenticator).
                                </p>
                                
                                <div style={{ textAlign: 'center', marginBottom: '1.5rem', padding: '1rem', background: 'white', display: 'inline-block', borderRadius: '0.5rem' }}>
                                    {twoFAData?.qrCode && <img src={twoFAData.qrCode} alt="2FA QR Code" />}
                                </div>

                                <div style={{ marginBottom: '1.5rem' }}>
                                    <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                                        2. If you cannot scan the QR code, enter this secret key manually:
                                    </p>
                                    <code style={{ display: 'block', padding: '0.75rem', background: 'var(--bg-dark)', color: 'white', borderRadius: '0.25rem', fontSize: '1rem', textAlign: 'center', letterSpacing: '0.1rem' }}>
                                        {twoFAData?.secret}
                                    </code>
                                </div>

                                <div>
                                    <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                                        3. Enter the 6-digit code from your app to verify:
                                    </p>
                                    <div style={{ display: 'flex', gap: '1rem' }}>
                                        <input
                                            type="text"
                                            className="input"
                                            placeholder="000000"
                                            value={twoFAToken}
                                            onChange={(e) => setTwoFAToken(e.target.value)}
                                            maxLength="6"
                                            style={{ maxWidth: '150px' }}
                                        />
                                        <button 
                                            className="btn btn-primary" 
                                            onClick={handleEnable2FA}
                                            disabled={twoFALoading || twoFAToken.length !== 6}
                                        >
                                            {twoFALoading ? 'Verifying...' : 'Enable 2FA'}
                                        </button>
                                        <button 
                                            className="btn btn-secondary" 
                                            onClick={() => setShow2FASetup(false)}
                                            disabled={twoFALoading}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Profile;
